import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { object, string, boolean } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  useTheme,
  Switch,
} from "@mui/material";
import {
  User,
  Mail,
  MapPin,
} from 'lucide-react';
import { createUser } from "./UserService";
import {
  formatDateForInput,
} from "../../utils/functions/Helpers";
import Constants from "../../utils/constants/Constants";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { tokens } from "../../theme";

const userSchema = object().shape({
  id: string().notRequired(),
  userId: string().notRequired(),
  firstName: string().required("First name is required"),
  lastName: string().required("Last name is required"),
  email: string().email("Invalid email format.").required("Email is required"),
  phone: string().required("Phone is required"),
  username: string().notRequired(),
  gender: string().notRequired(),
  dateOfBirth: string().required("Date of birth is required"),
  street: string().required("Street is required"),
  city: string().required("City is required"),
  state: string().required("State is required"),
  zipCode: string().required("Zip is required"),
  enabled: boolean().notRequired(),
});

const buildUserFormValues = (userToEdit) => ({
  id: userToEdit?.id ?? null,
  userId: userToEdit?.userId ?? "",
  username: userToEdit?.username ?? "",
  firstName: userToEdit?.firstName ?? "",
  lastName: userToEdit?.lastName ?? "",
  email: userToEdit?.email ?? "",
  phone: userToEdit?.phone ?? "",
  dateOfBirth: userToEdit?.dateOfBirth
    ? formatDateForInput(userToEdit.dateOfBirth)
    : "",
  gender: userToEdit?.gender ?? "",
  street: userToEdit?.street ?? "",
  city: userToEdit?.city ?? "",
  state: userToEdit?.state ?? "",
  zipCode: userToEdit?.zipCode ?? "",
  enabled: userToEdit?.enabled ?? true,
});

const UserForm = (props) => {
  const {
    userToEdit,
    updateUser,
    setState,
    toggleModal,
    isUpdating = false,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [apiError, setApiError] = useState(false);
  const setIsLoading = useLoadingSpinner();

  const {
    control,
    clearErrors,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: buildUserFormValues(userToEdit),
  });

  useEffect(() => {
    reset(buildUserFormValues(userToEdit));
  }, [userToEdit, reset]);

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal !== undefined && toggleModal(false);
  };

  const onSubmit = async (data) => {
    const formattedData = {
      ...data,
      dateOfBirth: formatDateForInput(data.dateOfBirth)
    };

    setIsLoading(true);
    if (isUpdating) {
      await updateUser(formattedData, setIsLoading, setState, setApiError);
    } else {
      await createUser(formattedData, setIsLoading, setState, setApiError);
    }

    if (!apiError) {
      reset();
      setState((prev) => !prev);
      toggleModal !== undefined && toggleModal(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h3" fontWeight="700" sx={{ mb: 1, color: colors.grey[100], fontFamily: "Josefin Sans" }}>
              {isUpdating ? "Update User Profile" : "Register New User"}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.grey[300], mb: 2 }}>
              Please provide the {isUpdating ? 'updated' : 'necessary'} details for the user account.
            </Typography>
          </Grid>

          {/* Personal Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <User size={18} /> Personal Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name"
              {...register("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name"
              {...register("lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              {...register("dateOfBirth")}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth?.message}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.gender}>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="gender-label"
                    label="Gender"
                    {...field}
                    sx={{
                      borderRadius: "12px",
                      color: colors.grey[100],
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                      "& .MuiSvgIcon-root": { color: colors.grey[100] }
                    }}
                  >
                    <MenuItem value="" sx={{ color: colors.grey[100] }}>Select Gender</MenuItem>
                    {Constants.GENDERS.map((gender) => (
                      <MenuItem key={gender} value={gender} sx={{ color: colors.grey[100] }}>{gender}</MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.gender?.message}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Contact Information Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Mail size={18} /> Contact & Account
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="123-456-7890"
              {...register("phone")}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: '12px', bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }}>
              <Box>
                <Typography variant="body1" fontWeight="600" color={colors.grey[100]}>Account Status</Typography>
                <Typography variant="body2" color={colors.grey[400]}>Enable or disable this user's access to the system</Typography>
              </Box>
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    color="secondary"
                  />
                )}
              />
            </Box>
          </Grid>

          {/* Address Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapPin size={18} /> Location Details
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              {...register("street")}
              error={!!errors.street}
              helperText={errors.street?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              {...register("city")}
              error={!!errors.city}
              helperText={errors.city?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.state}>
              <InputLabel id="state-label">State</InputLabel>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="state-label"
                    label="State"
                    {...field}
                    sx={{
                      borderRadius: "12px",
                      color: colors.grey[100],
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.greenAccent[500] },
                      "& .MuiSvgIcon-root": { color: colors.grey[100] }
                    }}
                  >
                    <MenuItem value="" sx={{ color: colors.grey[100] }}>Select State</MenuItem>
                    {Constants.STATES.map((state) => (
                      <MenuItem key={state} value={state} sx={{ color: colors.grey[100] }}>{state}</MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.state?.message}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Zip Code"
              {...register("zipCode")}
              error={!!errors.zipCode}
              helperText={errors.zipCode?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              {isUpdating ? 'Update User' : 'Register User'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

UserForm.propTypes = {
  userToEdit: PropTypes.object,
  updateUser: PropTypes.func,
  setState: PropTypes.func,
  toggleModal: PropTypes.func,
  isUpdating: PropTypes.bool,
};

export default UserForm;
